import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api, type Outcome } from '../api';
import { useApi } from '../hooks';
import { Alert, Badge, buttonClass } from '@ledgerloom/ui';
import { badgeTone, ErrorBanner, outcomeMessage, outcomeTone, useToast } from './ui';
import { STATE, type AreaState } from '../pages/Welcome';

// Cards agents posted for the person (docs/plans/agent-notices-plan.md). Home
// shows every one; an agent's page shows its own with the steps; the page a
// notice links to shows it with the steps and no button. Title, line, and
// button words are an agent's text, shown as text. The counts are the
// server's. Patterns: user-feedback/notification, user-feedback/progress-indicator.

export interface Notice {
  id: string; agent_id: string; agent_name: string | null; title: string; body: string | null; link: string | null; link_label: string | null;
  created_at: string; expires_at: string;
  progress: { source: string; done: number; total: number; steps: Array<{ title: string; state: AreaState; done: boolean }> } | null;
}

export const useNotices = (agentId?: string) => useApi<Notice[]>(`/v1/notices${agentId ? `?agent_id=${encodeURIComponent(agentId)}` : ''}`);

export function Notices({ notices, onChanged, steps = false, here }: { notices: Notice[]; onChanged: () => void; steps?: boolean; here?: string }) {
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  async function dismiss(n: Notice) {
    if (busy) return;
    setBusy(n.id); setError(null);
    try {
      const out = await api<Outcome>(`/v1/notices/${n.id}/dismiss`, { method: 'POST' });
      toast(outcomeMessage(out, 'Dismissed'), outcomeTone(out));
      onChanged();
    } catch (e) { setError(e); } finally { setBusy(null); }
  }
  if (!notices.length) return null;
  return (
    <>
      <ErrorBanner error={error} />
      {notices.map((n) => {
        const p = n.progress;
        // On the page it links to, the button would lead nowhere new.
        const link = n.link && n.link.split('?')[0] !== here ? n.link : null;
        return (
          <section key={n.id} aria-label={n.title}>
            <Alert
              tone="info"
              title={`${n.title}${p ? `: ${p.done} of ${p.total} done.` : ''}`}
              action={link ? <Link className={buttonClass({ size: 'sm' })} to={link}>{n.link_label ?? 'Open'}</Link> : undefined}
              onDismiss={() => void dismiss(n)}
            >
              {n.body}
              {n.agent_name && <span className="text-muted"> · from <Link to={`/agents/${encodeURIComponent(n.agent_id)}`}>{n.agent_name}</Link></span>}
              {steps && p && (
                <ul className="mt-2 grid" aria-label={`${n.title} steps`}>
                  {p.steps.map((s) => (
                    <li key={s.title} className="flex items-center gap-3 border-t border-line-subtle py-2 first:border-t-0">
                      <span className="flex-1 text-foreground">{s.title}</span>
                      <Badge tone={badgeTone(STATE[s.state].tone)}>{STATE[s.state].label}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </Alert>
          </section>
        );
      })}
    </>
  );
}

// In the shell: the notices that link to the page open now, with their steps.
// Home and an agent's page show their own.
export function NoticesHere() {
  const { pathname } = useLocation();
  const own = pathname === '/' || pathname.startsWith('/agents/');
  const all = useApi<Notice[]>(own ? null : '/v1/notices', [pathname]);
  const here = (all.data ?? []).filter((n) => n.link?.split('?')[0] === pathname);
  if (own || !here.length) return null;
  return <div className="mb-4 grid gap-3"><Notices notices={here} onChanged={() => void all.reload()} steps here={pathname} /></div>;
}
